/**
 * Zentrale Wrapper-Funktionen für haptisches Feedback (Vibration).
 *
 * - Nutzt `navigator.vibrate`, die unter Android (Chromium-basierte Browser)
 *   und in installierten TWAs (wie unserem APK) zuverlässig funktioniert.
 * - Unter iOS/Desktop ist die API nicht verfügbar — dann no-op.
 * - Respektiert das System-Setting "Reduzierte Bewegung", damit User mit
 *   Vestibulär-Empfindlichkeit nicht ständig vibriert werden.
 * - Optional pro User abschaltbar via localStorage-Flag `haptics_enabled`
 *   (Default: an). So lässt sich später leicht ein Profil-Toggle einbauen.
 *
 * Pattern-Konvention:
 *   tap()     — kurzer Bestätigungs-Tick (FAB, Tab-Wechsel)
 *   success() — speichern erfolgreich
 *   warning() — Lösch-Bestätigung, Konflikt
 *   error()   — Fehler-Toast
 */

const STORAGE_KEY = 'shiftsync.haptics.enabled';

function canVibrate(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.vibrate !== 'function') return false;

  // System-Setting "Bewegung reduzieren" respektieren
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }
  }

  // User-Override über Profil-Setting (Default an)
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'false') return false;
  } catch {
    // localStorage kann in Private-Mode werfen — dann einfach durchlassen.
  }

  return true;
}

function vibrate(pattern: number | number[]): void {
  if (!canVibrate()) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // Manche Browser werfen bei zu langem Pattern — ignorieren.
  }
}

/** Kurzes „Tap"-Feedback, z. B. beim Öffnen eines Dialogs / FAB-Drücken. */
export function hapticTap(): void {
  vibrate(15);
}

/** Bestätigung nach erfolgreichem Speichern. Zwei kurze Ticks. */
export function hapticSuccess(): void {
  vibrate([10, 40, 20]);
}

/** Warnsignal — z. B. Lösch-Bestätigung, sichtbarer Konflikt. */
export function hapticWarning(): void {
  vibrate([30, 60, 30]);
}

/** Fehler — kräftiger Buzz. */
export function hapticError(): void {
  vibrate([60, 50, 60]);
}

/** Setting umschalten — für späteren Profil-Toggle. */
export function setHapticsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // ignore
  }
}

export function isHapticsEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}
